import { z } from "zod"
import { Errors } from "../../errors"
import { S3_UPLOAD_INPUT_FORMATS } from "../../shared/files"
import { createFilterBySchema } from "../../shared/filter_dtos"
import { PaginationSchema } from "../../shared/pagination_dtos"
import { QuerySchema } from "../../shared/query_dtos"
import { createSortSchema } from "../../shared/sort_dtos"

const whatsappTemplatesSortFields = [
  "name",
  "status",
  "language",
  "id",
  "updatedAt",
  "lastUsedAt",
] as const
export const WhatsappTemplatesSortSchema = createSortSchema(
  whatsappTemplatesSortFields,
  "name",
  "asc",
)
export const WhatsappTemplatesStatusFilter = ["APPROVED", "PENDING", "REJECTED"] as const
export const WhatsappTemplatesCampaignFilter = {
  Unassigned: "unassigned",
} as const

export const WhatsappTemplatesFilterBySchema = createFilterBySchema([
  {
    key: "isOneTime",
    value: z
      .union([z.literal("true"), z.literal("false"), z.literal("all"), z.boolean()])
      .transform((value) => {
        if (value === "all") return value
        return typeof value === "boolean" ? value : value === "true"
      })
      .optional(),
  },
  {
    key: "name",
    value: z.string().optional(),
  },
  {
    key: "status",
    value: z.enum(WhatsappTemplatesStatusFilter).optional(),
  } as const,
  {
    key: "language",
    value: z.string().optional(),
  } as const,
  {
    key: "campaignId",
    value: z.string().optional(),
  },
] as const)

export const GetWhatsappTemplatesSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(WhatsappTemplatesFilterBySchema.shape)
    .extend(WhatsappTemplatesSortSchema.shape).shape,
  excludeIds: z
    .string()
    .regex(/^(?:\d+(?:,\d+)*)?$/)
    .optional(),
})

export const GetWhatsappTemplateByIdSchema = z.object({ id: z.string().transform(BigInt) })
export const GetWhatsappNumberByIdSchema = z.object({ id: z.string().transform(BigInt) })

export const WhatsappTemplateBodySchema = z.string().refine(
  (body) => {
    const trimmedBody = body.trim()
    const placeholder = /^\{\{\w+\}\}/
    const trailingPlaceholder = /\{\{\w+\}\}$/

    return !placeholder.test(trimmedBody) && !trailingPlaceholder.test(trimmedBody)
  },
  { message: Errors.WHATSAPP_TEMPLATE_VARIABLE_AT_EDGE },
)

export const UpdateWhatsappNumberCompanySchema = z.object({
  companyId: z.string().transform(BigInt),
})

export const WhatsappTemplateButtonUrlTypeSchema = z.enum(["DYNAMIC", "STATIC"])

export const MAX_WHATSAPP_TEMPLATE_URL_BUTTONS = 2

export const WhatsappTemplateButtonSchema = z
  .object({
    text: z.string().min(1),
    urlType: WhatsappTemplateButtonUrlTypeSchema,
    url: z.string().nullish(),
    variableName: z.string().nullish(),
    variableDescription: z.string().nullish(),
  })
  .superRefine((button, ctx) => {
    if (button.urlType === "STATIC") {
      const isValidUrl = z.url({ protocol: /^https?$/ }).safeParse(button.url).success

      if (!isValidUrl || /[{}]/.test(button.url ?? "")) {
        ctx.addIssue({ code: "custom", path: ["url"], message: Errors.INVALID_URL })
      }

      return
    }

    if (!/^[\w_]+$/.test(button.variableName ?? "")) {
      ctx.addIssue({
        code: "custom",
        path: ["variableName"],
        message: Errors.WHATSAPP_TEMPLATE_VARIABLE_NAME_INVALID,
      })
    }
  })

const WhatsappTemplateButtonsSchema = z
  .array(WhatsappTemplateButtonSchema)
  .max(MAX_WHATSAPP_TEMPLATE_URL_BUTTONS)
  .superRefine((buttons, ctx) => {
    buttons.forEach((button, index) => {
      if (button.urlType !== "DYNAMIC" || !button.variableName) return

      const firstIndex = buttons.findIndex(
        (other) => other.urlType === "DYNAMIC" && other.variableName === button.variableName,
      )

      if (firstIndex === index) return

      ctx.addIssue({
        code: "custom",
        path: [index, "variableName"],
        message: Errors.WHATSAPP_TEMPLATE_VARIABLE_NAME_INVALID,
      })
    })
  })

export const CreateWhatsappTemplateSchema = z.object({
  template: z.object({
    name: z.string(),
    language: z.string(),
    category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]),
    header: z.string().optional(),
    body: WhatsappTemplateBodySchema,
    footer: z.string().optional(),
    buttons: WhatsappTemplateButtonsSchema.optional(),
    whatsappTemplateVariables: z
      .array(
        z.object({
          paramName: z.string().refine((val) => /^[\w_]+$/.test(val), {
            message: Errors.WHATSAPP_TEMPLATE_VARIABLE_NAME_INVALID,
          }),
          description: z.string().nullish(),
          example: z
            .string()
            .min(1, { message: Errors.WHATSAPP_TEMPLATE_VARIABLE_EXAMPLE_REQUIRED }),
        }),
      )
      .optional(),
  }),
  media: z
    .object({
      url: z.string().nullish(),
      format: S3_UPLOAD_INPUT_FORMATS.nullish(),
    })
    .nullish(),
})

export const UpdateWhatsappTemplateBodySchema = z.object({
  template: z.object({
    header: z.string().optional(),
    body: WhatsappTemplateBodySchema,
    footer: z.string().optional(),
    buttons: WhatsappTemplateButtonsSchema.optional(),
    whatsappTemplateVariables: z
      .array(
        z.object({
          paramName: z.string().refine((val) => /^[a-z_]+$/.test(val), {
            message: Errors.WHATSAPP_TEMPLATE_VARIABLE_NAME_INVALID,
          }),
          description: z.string().nullish(),
          example: z
            .string()
            .min(1, { message: Errors.WHATSAPP_TEMPLATE_VARIABLE_EXAMPLE_REQUIRED }),
        }),
      )
      .optional(),
  }),
  media: z
    .object({
      url: z.string().nullish(),
      format: S3_UPLOAD_INPUT_FORMATS.nullish(),
    })
    .nullish(),
})

export const CreateIntegrationConfigurationSchema = z.object({
  accountId: z.string(),
  accessToken: z.string(),
})

export const RegisterPhoneNumberSchema = z.object({
  country: z.string().optional(),
  phoneNumber: z.string().optional(),
  cert: z.string().optional(),
  phoneId: z.string(),
})

// Whatsapp opening messages

export const whatsappOpeningMessagesSortFields = [
  "identifier",
  "locale",
  "archived",
  "text",
  "createdAt",
] as const
export const WhatsappOpeningMessagesSortSchema = createSortSchema(
  whatsappOpeningMessagesSortFields,
  "identifier",
  "asc",
)

export const WhatsappOpeningMessagesFilterBySchema = createFilterBySchema([
  {
    key: "identifier",
    value: z.string().optional(),
  },
  {
    key: "locale",
    value: z.string().optional(),
  } as const,
  {
    key: "campaignId",
    value: z.string().optional(),
  },
  {
    key: "archived",
    value: z.string().optional(),
  },
] as const)

export const GetWhatsappOpeningMessagesSchema = z.object({
  ...PaginationSchema.extend(QuerySchema.shape)
    .extend(WhatsappOpeningMessagesFilterBySchema.shape)
    .extend(WhatsappOpeningMessagesSortSchema.shape).shape,
})

export const UpsertWhatsappOpeningMessageSchema = z.object({
  locale: z.string(),
  identifier: z.string(),
  text: z.string().refine((val) => val.includes("{{identifier}}"), {
    message: Errors.OPENING_MESSAGE_TEXT_NO_IDENTIFIER_VARIABLE,
  }),
  archivedAt: z.string().optional(),
})

export const GetWhatsappOpeningMessageSchema = z.object({
  id: z.string().transform(BigInt),
})

export const EmbeddedSignupSchema = z.object({
  phoneId: z.string().min(1).optional(),
  businessId: z.string().min(1),
  code: z.string().min(1),
  isCoexistence: z.boolean().default(false),
  companyId: z.string().transform(BigInt).optional(),
})

export const ConnectWhatsappCoexistenceSchema = z.object({
  phoneId: z.string().min(1),
  companyId: z.string().transform(BigInt),
})

export const EmbeddedSignupSessionEventSchema = z.object({
  type: z.literal("WA_EMBEDDED_SIGNUP"),
  event: z.enum(["FINISH", "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING", "CANCEL", "ERROR"]),
  data: z
    .object({
      phone_number_id: z.string().optional(),
      waba_id: z.string().optional(),
      error_message: z.string().optional(),
    })
    .optional(),
})

export const TranslateWhatsappTemplateSchema = z.object({
  languages: z.array(z.string()),
})

export const ImproveWhatsappTemplateSchema = z.object({
  body: z.string(),
  language: z.string(),
})

export const CreateTranslatedTemplatesSchema = z.object({
  id: z.string().transform(BigInt),
  name: z.string(),
  mediaUrl: z.string().nullish(),
  mediaFormat: S3_UPLOAD_INPUT_FORMATS.nullish(),
  translations: z.array(
    z.object({
      language: z.string(),
      body: WhatsappTemplateBodySchema,
      buttonTexts: z.array(z.string().min(1)).nullish(),
    }),
  ),
})

export const UploadMediaWhatsappTemplateFileSchema = z.object({
  file: z.object({
    data: z.unknown().refine((value) => Buffer.isBuffer(value), {
      error: "Expected a Buffer",
    }),
    name: z.string(),
    mimetype: z.string(),
  }),
})
